import { AddinRoleAdditionMode, ProductForSaleDTO, ProductOrigin, Res, SoldProductType, TicketLineDTO, TicketLineStatus } from 'core/api';
import { CompositionResult, CompositionState } from '../product-composition/product/Composition';
import { CatalogueFacade } from 'pos/facades';
import * as SalesStore from '../SalesStore';
import { Result } from 'core/infrastructure';
import { Message, Modal } from 'core/ui-next/modal';
import { ProductWizardScreen } from '../product-composition/product/ProductWizardScreen';
import { RestaurantFacade } from 'pos/facades';

import * as AddinComposer from '../product-composition/product/AddinComposer';
import { getAddinRolesForAddition, AddinsStep, RemovedIngredientsStep, StepDefinition, NotesStep } from '../product-composition/product';


export async function editLineNotes(line: TicketLineDTO): Promise<Result<CompositionResult>> {
	return editLineComposition(line, NotesStep, async (_line, _product) => true);
}

export async function editLineRemovedIngredients(line: TicketLineDTO): Promise<Result<CompositionResult>> {
	return editLineComposition(line, RemovedIngredientsStep, async (line, product) => {

		if (line.Status !== TicketLineStatus.New) {
			await Message.show(Res.Msg.MSG_CANNOT_ADJUST_INGREDIENTS_OF_ORDERED_LINE());
			return false;
		}

		if (product.Origin !== ProductOrigin.RecipeAuto || line.Type === SoldProductType.MenuHeader) {
			await Message.show(Res.Msg.MSG_PRODUCT_NOT_ALLOW_ADJUST_INGREDIENTS());
			return false;
		}
		return true;
	});
}

export async function editLineAddins(line: TicketLineDTO): Promise<Result<CompositionResult>> {
	return editLineComposition(line, AddinsStep, async (line, product) => {

		if (line.Status !== TicketLineStatus.New) {
			await Message.show(Res.Msg.MSG_CANNOT_CHANGE_ADDINS_OF_ORDERED_LINE());
			return false;
		}

		if (!product.HasAddins) {
			await Message.show(Res.Msg.MSG_CANNOT_CHANGE_ADDINS_BECAUSE_PRODUCT_HAS_NOT_ADDINS());
			return false;
		}

		return true;
	});
}

async function editLineComposition(line: TicketLineDTO, step: StepDefinition, canBeEdited: (line: TicketLineDTO, product: ProductForSaleDTO) => Promise<boolean>): Promise<Result<CompositionResult>> {

	const product = await CatalogueFacade.getSaleableAsMainBySaleFormatId(line.Product.SaleFormatId, SalesStore.getTicket().Id);

	if (!(await canBeEdited(line, product)))
		return Result.error();

	const { mode, roles } = product.HasAddins
		? await getAddinRolesForAddition(product, line.Quantity)
		: { mode: AddinRoleAdditionMode.UseSameAddinForAll, roles: [] };

	const initialComposition: CompositionState = {
		selectedProduct: product,
		addinsSelection: {
			composition:
				roles.length > 0
					? await AddinComposer.buildComposition({ mainProduct: product, roles, previousAddins: line.Addins || [], addinMenuGroupName: '', ticketId: SalesStore.getTicket().Id }, CatalogueFacade.getSaleableAsAddinByRole)
					: AddinComposer.createEmptyAddinComposition(product),
			mode
		},
		color: line.Product.Color,
		size: line.Product.Size,
		lotSelection: {
			selected: line.Product.Lot,
			criteria: '',
			searchedLots: [],
			mode: 'assign',
			showOnlyWithStock: false
		},
		preparationNotes: line.Notes.join('\n'),
		price: line.Product.Price,
		quantity: line.Quantity,
		removedIngredients: line.RemovedIngredients
	};

	const profile = await RestaurantFacade.getDisplayProfile();

	const result = await Modal.show(ProductWizardScreen, {
		params: {
			product,
			groupName: line.Product.GroupName,
			preparationOrderId: line.Product.PreparationOrderId,
			preparationTypeId: line.Product.PreparationTypeId,
			type: line.Type
		},
		initialComposition,
		initialStepsStatus: 'review',
		initialSteps: [step],
		profile
	});

	if (!result.ok)
		return Result.error();

	if (mode === AddinRoleAdditionMode.ChooseDifferentAddinPerUnit) {
		const allAddins = result.value
			.filter(c => c.addins.length > 0)
			.flatMap(c => Array.from({ length: c.quantity }, () => c.addins[0]));
		return Result.ok({ ...result.value[0], addins: allAddins });
	}

	return Result.ok(result.value[0]);
}